import React, {
  KeyboardEventHandler,
  RefObject,
  useEffect,
  useState,
} from "react";
import { Textarea } from "@mui/joy";
import { isMobile } from "react-device-detect";
import { theme } from "styles/theme";

interface TextareaOnboardingProps {
  placeholder?: string;
  initialValue?: string;
  handleChange: (value: string) => void;
  isFocused: boolean;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  emojiDisabled?: boolean;
  readOnly?: boolean;
  inputRef?: RefObject<HTMLTextAreaElement>;
  textAlign: "start" | "center";
  numbersOnly?: boolean;
  maxLength?: number;
}

export default function TextareaOnboarding({
  placeholder,
  initialValue,
  handleChange,
  isFocused,
  setIsFocused,
  emojiDisabled,
  readOnly,
  inputRef,
  textAlign,
  numbersOnly,
  maxLength,
}: TextareaOnboardingProps) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (!isFocused) {
      setValue(initialValue);
    }
  }, [initialValue, isFocused]);

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (
      !isMobile &&
      (e.code === "Enter" ||
        e.code === "NumpadEnter" ||
        e.key === "Enter" ||
        e.key === "NumpadEnter")
    ) {
      // 필요 시 엔터 입력 로직
    }
  };

  // 허용 범위 ㄱ-힣(U+3131~U+D7A3) 안에 ㆍ(아래아, U+318D)가 포함되므로 별도로 제거
  const disabled_regex = /[^a-zA-Z0-9ㄱ-힣]|ㆍ/g;

  return (
    <Textarea
      placeholder={placeholder}
      value={value || ""}
      maxRows={1}
      onChange={(e) => {
        let newValue = e.target.value;

        // ➌ numbersOnly가 true이면 숫자 이외의 문자를 제거
        if (numbersOnly) {
          newValue = newValue.replace(/[^0-9]/g, "");
        }
        // 그렇지 않고 emojiDisabled가 true이면 기존 로직 수행
        else if (emojiDisabled) {
          newValue = newValue.replace(disabled_regex, "");
        }

        setValue(newValue);
        handleChange(newValue);
      }}
      readOnly={readOnly || false}
      minRows={1}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onKeyDown={handleKeyDown}
      slotProps={{
        textarea: {
          ref: inputRef,
          // ➍ numbersOnly일 때 모바일에서 숫자 키패드를 띄우도록 설정
          inputMode: numbersOnly ? "numeric" : "text",
          maxLength: maxLength,
        },
      }}
      sx={{
        width: "100%",
        boxShadow: "none",
        backgroundColor: "transparent",
        padding: "0 20px",
        border: "none",
        minWidth: 0,
        minHeight: "24px",
        "--joy-focus-thickness": "0px",
        "--variant-borderWidth": "0px",
        "--Textarea-gap": "0px",
        "& textarea": {
          textAlign: `${textAlign}`,
          padding: 0,
          margin: 0,
          fontWeight: 500,
          color: theme.vars.palette.Content.Strong,
          whiteSpace: "nowrap",
          overflowX: "auto",
          resize: "none",
        },
        "& textarea::placeholder": {
          textAlign: `${textAlign}`,
        },
      }}
    />
  );
}
